<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// The always-visible list of EVERY screen (greeting pinned first, then every
// reply screen by direct enumeration — never by walking button links). This is
// the primary navigator: a saved screen can never be hidden, so orphans are
// always selectable, editable and deletable.
const props = defineProps({
  screens: { type: Array, default: () => [] },
  currentId: { type: String, default: '' },
  reachableIds: { type: Set, default: () => new Set() },
  // screenId -> ["البداية", "شاشة «X»", "قالب Y"]: the doors into that screen.
  reachSources: { type: Map, default: () => new Map() },
  rootButtonCount: { type: Number, default: 0 },
  noEntryButtons: { type: Boolean, default: false },
});
const emit = defineEmits([
  'select',
  'addScreen',
  'removeScreen',
  'linkFromGreeting',
]);
const { t } = useI18n();

const MAX_BUTTONS = 3;
const greeting = computed(() => props.screens.find(s => s.isRoot));
const replyScreens = computed(() => props.screens.filter(s => !s.isRoot));
// A screen with no name can't be linked or saved yet — its own state, distinct
// from orphan (a NAMED screen nothing opens). This keeps the badge in sync with
// the header count, the warnings, and the flow map (all of which need a title).
const needsName = screen => !screen.isRoot && !screen.title.trim();
const isOrphan = screen =>
  !screen.isRoot && screen.title.trim() && !props.reachableIds.has(screen.id);
const orphanCount = computed(() => replyScreens.value.filter(isOrphan).length);
// «متوصّلة» alone tells the reader nothing they can verify — say WHERE from, and
// put the full list on the tooltip when a screen has more than one door.
const reachFrom = screen => props.reachSources.get(screen.id) || [];
const reachLabel = screen => {
  const list = reachFrom(screen);
  if (!list.length) return '';
  return list.length === 1 ? list[0] : `${list[0]} +${list.length - 1}`;
};
const snippet = text => {
  const clean = (text || '').replace(/\s+/g, ' ').trim();
  return clean.length > 46 ? `${clean.slice(0, 46)}…` : clean;
};
</script>

<template>
  <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
    <!-- header + count summary -->
    <div class="mb-3 flex items-center justify-between gap-2">
      <div>
        <h3 class="text-sm font-extrabold text-n-slate-12">
          {{ t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.TITLE') }}
        </h3>
        <p class="mt-0.5 text-[11.5px] font-medium text-n-slate-10">
          {{ t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.SUB') }}
        </p>
      </div>
      <span
        v-if="orphanCount > 0"
        class="shrink-0 rounded-full bg-n-amber-3 px-2.5 py-1 text-[11px] font-extrabold text-n-amber-11"
      >
        {{
          t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.COUNT_SUMMARY', {
            total: replyScreens.length,
            orphans: orphanCount,
          })
        }}
      </span>
      <span
        v-else
        class="shrink-0 rounded-full bg-n-teal-3 px-2.5 py-1 text-[11px] font-extrabold text-n-teal-11"
      >
        {{ t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.ALL_LINKED_OK') }}
      </span>
    </div>

    <div class="flex flex-col gap-2">
      <!-- greeting row (pinned) -->
      <button
        v-if="greeting"
        type="button"
        class="flex items-center gap-3 rounded-xl border p-3 text-right transition-all"
        :class="
          currentId === greeting.id
            ? 'border-n-brand bg-n-brand/10'
            : 'border-n-weak bg-n-alpha-black1 hover:border-n-strong'
        "
        @click="emit('select', greeting.id)"
      >
        <span
          class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-brand/15 text-n-brand"
        >
          <span class="i-lucide-house size-[18px]" />
        </span>
        <span class="min-w-0 flex-1">
          <span class="block text-[13px] font-extrabold text-n-slate-12">
            {{ t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.GREETING_ROW') }}
          </span>
          <span class="block text-[11px] font-medium text-n-slate-10">
            {{ t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.GREETING_ROW_SUB') }}
          </span>
        </span>
        <span
          v-if="noEntryButtons"
          class="shrink-0 rounded-full bg-n-ruby-3 px-2 py-1 text-[10.5px] font-extrabold text-n-ruby-11"
        >
          {{ t('AUTO_REPLY_BOT_SETTINGS.VALIDATION.NO_ENTRY_BUTTONS_BADGE') }}
        </span>
      </button>

      <!-- reply rows (every screen, incl orphans) -->
      <div
        v-for="screen in replyScreens"
        :key="screen.id"
        class="flex items-stretch gap-2 rounded-xl border p-2.5 transition-all"
        :class="
          currentId === screen.id
            ? 'border-n-brand bg-n-brand/10'
            : 'border-n-weak bg-n-alpha-black1 hover:border-n-strong'
        "
      >
        <button
          type="button"
          class="flex min-w-0 flex-1 items-center gap-3 text-right"
          @click="emit('select', screen.id)"
        >
          <span
            class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-slate-3 text-n-slate-11"
          >
            <span class="i-lucide-message-square-text size-[18px]" />
          </span>
          <span class="min-w-0 flex-1">
            <span
              class="block truncate text-[13px] font-extrabold"
              :class="
                screen.title.trim() ? 'text-n-slate-12' : 'text-n-slate-9'
              "
            >
              {{
                screen.title.trim() ||
                t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.UNNAMED')
              }}
            </span>
            <span
              class="block truncate text-[11px] font-medium text-n-slate-10"
            >
              {{ snippet(screen.text) }}
            </span>
          </span>
        </button>

        <div class="flex shrink-0 flex-col items-end justify-between gap-1.5">
          <span
            v-if="needsName(screen)"
            class="rounded-full bg-n-amber-3 px-2 py-0.5 text-[10px] font-extrabold text-n-amber-11"
          >
            {{ t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.NEEDS_NAME') }}
          </span>
          <span
            v-else-if="isOrphan(screen)"
            class="rounded-full bg-n-amber-3 px-2 py-0.5 text-[10px] font-extrabold text-n-amber-11"
          >
            {{ t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.ORPHAN_BADGE') }}
          </span>
          <span
            v-else
            v-tooltip="reachFrom(screen).join(' · ')"
            class="max-w-[220px] truncate rounded-full bg-n-teal-3 px-2 py-0.5 text-[10px] font-extrabold text-n-teal-11"
          >
            {{
              t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.REACH_OK_FROM', {
                source: reachLabel(screen),
              })
            }}
          </span>
          <div class="flex items-center gap-1">
            <button
              v-if="isOrphan(screen)"
              v-tooltip="
                rootButtonCount >= MAX_BUTTONS
                  ? t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.LINK_DISABLED_HINT')
                  : ''
              "
              type="button"
              :disabled="rootButtonCount >= MAX_BUTTONS"
              class="rounded-lg border border-n-weak px-2 py-1 text-[10.5px] font-bold text-n-slate-11 transition-colors hover:border-n-brand hover:text-n-brand disabled:cursor-not-allowed disabled:opacity-50"
              @click="emit('linkFromGreeting', screen.id)"
            >
              {{ t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.LINK_FROM_GREETING') }}
            </button>
            <button
              v-tooltip="t('AUTO_REPLY_BOT_SETTINGS.EDITOR.DELETE_SCREEN')"
              type="button"
              class="grid size-7 place-items-center rounded-lg text-n-slate-10 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11"
              @click="emit('removeScreen', screen.id)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
      </div>

      <p
        v-if="!replyScreens.length"
        class="rounded-xl border border-dashed border-n-weak px-3 py-4 text-center text-[12px] font-medium text-n-slate-10"
      >
        {{ t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.EMPTY') }}
      </p>

      <!-- add screen -->
      <button
        type="button"
        class="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-n-weak bg-n-alpha-black1 py-3 text-[13px] font-bold text-n-slate-11 transition-colors hover:border-n-brand hover:bg-n-brand/5 hover:text-n-brand"
        @click="emit('addScreen')"
      >
        <span class="i-lucide-plus size-4" />
        {{ t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.ADD_SCREEN') }}
      </button>
    </div>
  </div>
</template>
